// Calendar event sorting.

import type { CalendarEvent, CalendarDataCategoryResponse, Importance } from "../types";
import { CLASSIFICATION_ORDER, IMPORTANCE_ORDER } from "./constants";

function eventTime(datetime: string | null | undefined): number {
  if (!datetime) return 0;
  const time = new Date(datetime).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Sort calendar events by:
 *  1. occurs_at (chronological)
 *  2. classification (data > speaker > supply > earning > others)
 *  3. location (alphabetical)
 *  4. importance (high > normal > low)
 *  5. summary (alphabetical)
 */
export function sortCalendarEvents(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort((a, b) => {
    // 1. occurs_at
    const timeA = eventTime(a.occurs_at);
    const timeB = eventTime(b.occurs_at);
    if (timeA !== timeB) return timeA - timeB;

    // 2. classification priority
    const orderA = CLASSIFICATION_ORDER[a.classification || ""] ?? 999;
    const orderB = CLASSIFICATION_ORDER[b.classification || ""] ?? 999;
    if (orderA !== orderB) return orderA - orderB;

    // 3. location (event or its category)
    const catA = (a as { category?: CalendarDataCategoryResponse }).category;
    const catB = (b as { category?: CalendarDataCategoryResponse }).category;
    const locA = catA?.location || a.location || "";
    const locB = catB?.location || b.location || "";
    if (locA !== locB) return locA.localeCompare(locB);

    // 4. importance (per-event wins over category)
    const impA =
      IMPORTANCE_ORDER[
        (a as { importance?: Importance }).importance || catA?.importance || "normal"
      ];
    const impB =
      IMPORTANCE_ORDER[
        (b as { importance?: Importance }).importance || catB?.importance || "normal"
      ];
    if (impA !== impB) return impA - impB;

    // 5. summary
    return (a.summary || "").localeCompare(b.summary || "");
  });
}
